
import {
  defineComponent,
  PropType,
  reactive,
  ref,
  computed,
  watch,
  onMounted,
  nextTick,
} from 'vue';
import { useI18n } from 'vue-i18n';
import InputSwitch from 'primevue/inputswitch';
import InputText from 'primevue/inputtext';
import OverlayPanel from 'primevue/overlaypanel';
import Button from 'primevue/button';
import { UserState } from '@/composables/User';
import { fetchShopConfigurationsNest } from '@/composables/shop/ShopsConfiguration';
import { useUtmDropdownOptions, DropdownOption } from '@/composables/flows/useUtmDropdownOptions';

export interface UtmCustomParam {
  key: string;
  value: string;
}

export interface UtmData {
  utm_enabled: boolean;
  utm_use_custom: boolean;
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_id: string;
  utm_content: string;
  utm_term: string;
  utm_source_enabled: boolean;
  utm_medium_enabled: boolean;
  utm_campaign_enabled: boolean;
  utm_id_enabled: boolean;
  utm_content_enabled: boolean;
  utm_term_enabled: boolean;
  utm_custom_params: UtmCustomParam[];
}

const BASE_I18N = 'automatedScenarios.actions.sending_channel.utm_config';

export default defineComponent({
  name: 'UtmConfig',

  components: {
    InputSwitch,
    InputText,
    OverlayPanel,
    Button,
  },

  props: {
    modelValue: {
      type: Object as PropType<UtmData>,
      required: true,
    },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const { dropdownOptions, loadCustomData } = useUtmDropdownOptions(t);

    const localData = reactive<UtmData>({
      utm_enabled: props.modelValue.utm_enabled ?? true,
      utm_use_custom: props.modelValue.utm_use_custom ?? false,
      utm_source: props.modelValue.utm_source ?? '',
      utm_medium: props.modelValue.utm_medium ?? '',
      utm_campaign: props.modelValue.utm_campaign ?? '',
      utm_id: props.modelValue.utm_id ?? '',
      utm_content: props.modelValue.utm_content ?? '',
      utm_term: props.modelValue.utm_term ?? '',
      utm_source_enabled: props.modelValue.utm_source_enabled ?? true,
      utm_medium_enabled: props.modelValue.utm_medium_enabled ?? true,
      utm_campaign_enabled: props.modelValue.utm_campaign_enabled ?? true,
      utm_id_enabled: props.modelValue.utm_id_enabled ?? false,
      utm_content_enabled: props.modelValue.utm_content_enabled ?? false,
      utm_term_enabled: props.modelValue.utm_term_enabled ?? false,
      utm_custom_params: props.modelValue.utm_custom_params ?? [],
    });

    // Param definitions
    const utmParams = [
      { key: 'source', valueKey: 'utm_source', enabledKey: 'utm_source_enabled', labelKey: `${BASE_I18N}.labels.source`, placeholder: 'shopimind' },
      { key: 'medium', valueKey: 'utm_medium', enabledKey: 'utm_medium_enabled', labelKey: `${BASE_I18N}.labels.medium`, placeholder: '{var=channel}' },
      { key: 'campaign', valueKey: 'utm_campaign', enabledKey: 'utm_campaign_enabled', labelKey: `${BASE_I18N}.labels.campaign`, placeholder: '{var=campaign_name}' },
      { key: 'id', valueKey: 'utm_id', enabledKey: 'utm_id_enabled', labelKey: `${BASE_I18N}.labels.id`, placeholder: '' },
      { key: 'content', valueKey: 'utm_content', enabledKey: 'utm_content_enabled', labelKey: `${BASE_I18N}.labels.content`, placeholder: '' },
      { key: 'term', valueKey: 'utm_term', enabledKey: 'utm_term_enabled', labelKey: `${BASE_I18N}.labels.term`, placeholder: '' },
    ];

    // Custom params management
    const addCustomParam = () => {
      localData.utm_custom_params.push({ key: '', value: '' });
    };

    const removeCustomParam = (index: number) => {
      localData.utm_custom_params.splice(index, 1);
    };

    // Variable picker state
    const variablePickerRef = ref();
    const variableFilter = ref('');
    const activeField = ref('');
    const activeCustomIndex = ref(-1);

    const filteredOptions = computed(() => {
      if (!variableFilter.value) return dropdownOptions.value;
      const q = variableFilter.value.toLowerCase();
      return dropdownOptions.value.filter(
        (opt) => opt.displayLabel.toLowerCase().includes(q) || opt.value.toLowerCase().includes(q),
      );
    });

    const openVariablePicker = (event: Event, fieldKey: string) => {
      activeField.value = fieldKey;
      activeCustomIndex.value = -1;
      variableFilter.value = '';
      variablePickerRef.value.toggle(event);
    };

    const openVariablePickerCustom = (event: Event, index: number) => {
      activeField.value = '';
      activeCustomIndex.value = index;
      variableFilter.value = '';
      variablePickerRef.value.toggle(event);
    };

    const selectVariable = (opt: DropdownOption) => {
      if (activeCustomIndex.value >= 0) {
        localData.utm_custom_params[activeCustomIndex.value].value = opt.value;
      } else {
        (localData as any)[activeField.value] = opt.value;
      }
      variablePickerRef.value.hide();
    };

    // Guard to prevent circular watcher updates
    let isEmitting = false;

    // Emit on changes
    watch(localData, () => {
      isEmitting = true;
      emit('update:modelValue', { ...localData });
      nextTick(() => { isEmitting = false; });
    }, { deep: true });

    // Sync from parent (only if not our own emission echoing back)
    watch(() => props.modelValue, (newVal) => {
      if (isEmitting) return;
      const keys = Object.keys(localData) as (keyof UtmData)[];
      keys.forEach((key) => {
        if (newVal[key] !== undefined && (localData as any)[key] !== newVal[key]) {
          (localData as any)[key] = newVal[key];
        }
      });
    }, { deep: true });

    // Track whether UTM is globally disabled at shop level
    const globalUtmDisabled = ref(false);

    onMounted(async () => {
      const shopId = UserState.activeShop?.id ?? 0;

      // Load global UTM active config
      try {
        const config = await fetchShopConfigurationsNest(shopId, ['shopRequiredDataUtm_activeConfig']);
        if (config) {
          const activeConfig = config.shopRequiredDataUtm_activeConfig;
          const isGloballyEnabled = activeConfig !== '0';
          globalUtmDisabled.value = !isGloballyEnabled;

          if (!isGloballyEnabled) {
            // UTM disabled globally → force off
            localData.utm_enabled = false;
          } else if (props.modelValue.utm_enabled === undefined || props.modelValue.utm_enabled === null) {
            // New operator, no explicit setting → default from global config
            localData.utm_enabled = true;
          }
        }
      } catch {
        // Default: not disabled
      }

      // Load custom data properties for dropdown
      await loadCustomData();
    });

    return {
      t,
      localData,
      globalUtmDisabled,
      utmParams,
      dropdownOptions,
      addCustomParam,
      removeCustomParam,
      variablePickerRef,
      variableFilter,
      filteredOptions,
      openVariablePicker,
      openVariablePickerCustom,
      selectVariable,
    };
  },
});
